import { createRouter, createWebHistory } from 'vue-router';
import routes from './routes';
import { useAuthStore } from 'stores/auth';

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ left: 0, top: 0 }),
});

import { ROLES, isLifeguardMobileUser, resolveLandingRoute } from '@/config/permissions';
import { useRosterMode } from '@/composables/useRosterMode';
import { isConfinedToInAppNotice } from '@/utils/inAppBrowser';

// Hard ceiling for Firebase Auth restore on cold boot. If onAuthStateChanged
// never fires (iOS Safari IndexedDB wipe, ITP eviction) or the Firestore user
// doc read hangs, we'd spin forever otherwise. 15s matches KioskPage watchdog.
const AUTH_INIT_TIMEOUT_MS = 15000;

router.beforeEach(async (to, _from, next) => {
  // Manifest selection lives in App.vue (role-aware) — single source of truth.

  const authStore = useAuthStore();

  if (!authStore.isReady) {
    try {
      await Promise.race([
        authStore.init(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('AUTH_INIT_TIMEOUT')), AUTH_INIT_TIMEOUT_MS),
        ),
      ]);
    } catch (err) {
      console.error('[Router] Auth init timed out or failed:', err);
      // Force /login so the user gets an actionable surface instead of a spinner.
      if (to.path !== '/login') {
        return next({ path: '/login', query: { redirect: to.fullPath } });
      }
    }
  }

  // In-app browsers (LINE / FB / IG / TikTok / WeChat): the Hub is never used
  // inside them — signed in or not, every page shows the login page's
  // 「請改用瀏覽器開啟」 card. main.ts has already asked the app to hand the page
  // to Safari / Chrome; this is what stays behind in the app's own window
  // (PO 2026-09-22). Pages that must run inside LINE are exempt.
  if (isConfinedToInAppNotice(to.path)) {
    if (to.path === '/login') return next();
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  const isAuthenticated = authStore.isAuthenticated;
  const userRoles = authStore.userRoles;

  // Handle redirection for authenticated users
  if (isAuthenticated && userRoles.length > 0) {
    // Teacher Mobile Redirect (One-time per session)
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    if (userRoles.includes(ROLES.TEACHER) && isMobile && !to.path.startsWith('/teacher/mobile')) {
      const hasBeenRedirected = sessionStorage.getItem('hasBeenRedirectedToMobile');
      if (to.path.startsWith('/teacher') && !hasBeenRedirected) {
        sessionStorage.setItem('hasBeenRedirectedToMobile', 'true');
        return next({ path: '/teacher/mobile' });
      }
    }

    if (to.path === '/login' || to.path === '/') {
      // Honor a preserved deep-link target (e.g. PWA home-screen icon →
      // /attendance/clock while logged out → guard stashes ?redirect=… below →
      // Google sign-in returns here). Without this, the guard discarded the
      // target and dropped the user on a role default instead of the punch
      // screen. Only same-origin absolute app paths are accepted (must start
      // with a single '/') to avoid open-redirect to external hosts.
      const redirectTarget = to.query.redirect;
      if (
        typeof redirectTarget === 'string' &&
        redirectTarget.startsWith('/') &&
        !redirectTarget.startsWith('//') &&
        redirectTarget !== '/login'
      ) {
        return next({ path: redirectTarget });
      }
      // Role landing (kiosk-only → /kiosk; teacher / 救生員 → /teacher/home; else
      // /home) — one rule in permissions.ts. The ?redirect= deep link above
      // (e.g. the payslip email's 查看完整薪資單) always wins.
      return next({ path: resolveLandingRoute(userRoles) });
    }
  }

  // Handle route access control
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  if (requiresAuth && !isAuthenticated) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  // Kiosk access control — only allow kiosk, admin, supervisor to access /kiosk
  if (to.path === '/kiosk' && userRoles.length > 0) {
    const canAccessKiosk = userRoles.some((r) =>
      [ROLES.KIOSK, ROLES.ADMIN, ROLES.SUPERVISOR].includes(r as typeof ROLES.KIOSK),
    );
    if (!canAccessKiosk) {
      return next('/home');
    }
  }

  // 救生員 have no /home dashboard: every bounce to /home (incl. the allowedRoles
  // fallback below) lands on their phone home instead. /teacher/home admits them,
  // so this cannot loop.
  if (to.path === '/home' && isAuthenticated && isLifeguardMobileUser(userRoles)) {
    return next({ path: '/teacher/home' });
  }

  // 救生員模式 (teacher + 救生員): the teacher mobile pages a 救生員 cannot open
  // (回饋, 學生紀錄, 操作說明) stay closed until 老師模式 — otherwise a back-swipe
  // or bookmark would show them with the 救生員 page role.
  if (
    isAuthenticated &&
    to.meta.roleContext === 'teacher-mobile' &&
    Array.isArray(to.meta.allowedRoles) &&
    !to.meta.allowedRoles.includes(ROLES.LIFEGUARD) &&
    useRosterMode().rosterMode.value === 'lifeguard'
  ) {
    return next({ path: '/teacher/mobile' });
  }

  // Declarative role-based access control — union model.
  // User passes if ANY of their roles is in the route's allowedRoles.
  const allowedRoles = to.meta.allowedRoles;
  if (
    allowedRoles &&
    (userRoles.length === 0 || !userRoles.some((r) => allowedRoles.includes(r)))
  ) {
    return next('/home');
  }

  next();
});

export default router;
